import { Component, Input, OnInit, OnChanges, ChangeDetectorRef, inject, SimpleChanges } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { marketingPodcastEntity } from 'mj_generatedentities';
import { runAction } from '../../shared/run-action.util';

type PodcastIntakeSource = 'Manual' | 'Box' | 'RSS';
const INTAKE_SOURCES: ReadonlyArray<PodcastIntakeSource> = ['Manual', 'Box', 'RSS'];

interface PodcastRow {
    ID: string;
    Name: string;
    BoxFolderRef: string | null;
    IntakeSource: string | null;
}

type SubmitState = 'idle' | 'reading' | 'uploading' | 'done' | 'error';

/**
 * Programs · Podcast upload (#940) — manual upload affordance under #programs.
 *
 * Lets an Operator drop an audio file (and optional transcript) directly into
 * the Box-intake pipeline. Calls the `Upload Manual Podcast Episode` action,
 * which handles Box upload + PodcastEpisode + ContentSource creation.
 *
 * Operator+ gating is enforced at the route level
 * (`marketing-workspace.routes.config.ts`) and at the action layer via
 * MarketingBaseAction. This component does not duplicate the gate.
 *
 * Design choices:
 * - Renders as a dedicated section under Programs rather than a button on the
 *   blog list — uploads create episodes, not blogs, and the operator workflow
 *   has steps (pick podcast, pick file, optionally paste transcript) that
 *   benefit from the full surface.
 * - Audio file is base64-encoded in the browser before submission. Cap at
 *   25 MB; larger files should use Box drop directly.
 * - Transcript is optional. With one, the episode goes straight to
 *   TranscriptReady. Without one, it lands in Pending and waits for diarize.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-programs-podcast-upload',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Programs · Podcast upload' + (companyName ? ' · ' + companyName : '')"
    title="Drop in an episode."
    sub="Manual fallback for the Box-folder intake. Uploads audio (and optional transcript) directly to the podcast pipeline.">
</mkt-ws-page-head>

<div *ngIf="loading" class="ppu__loading">
    <i class="fa-solid fa-spinner fa-spin"></i> Loading podcasts…
</div>

<ng-container *ngIf="!loading">
    <!--
        #938 / #940 follow-up — Inline create-podcast form. The original
        empty state told the operator to "create a marketing.Podcast row
        first" with no UX path; this gives them one without leaving the
        section. Operator+ gating is still enforced at the route level.
    -->
    <mkt-ws-card *ngIf="podcasts.length === 0" [hasHead]="true" [hasFoot]="true">
        <div head><span class="ppu__caps">Create a podcast</span></div>

        <p class="ppu__empty-lead">
            <i class="fa-solid fa-microphone-lines"></i>
            No podcasts configured for this company. Create one to start uploading episodes.
        </p>

        <div class="ppu__form">
            <label class="ppu__field">
                <span class="ppu__label">Podcast name <span class="ppu__req">*</span></span>
                <input class="ppu__input" type="text" [(ngModel)]="createForm.name" name="podcastName"
                       [disabled]="creating" maxlength="500" placeholder="e.g. Sidecar Sync" />
            </label>

            <label class="ppu__field">
                <span class="ppu__label">Description</span>
                <textarea class="ppu__input ppu__input--textarea" [(ngModel)]="createForm.description"
                          name="podcastDescription" [disabled]="creating" rows="3"
                          placeholder="Optional — short summary of what the podcast covers."></textarea>
            </label>

            <div class="ppu__field-row">
                <label class="ppu__field ppu__field--narrow">
                    <span class="ppu__label">Intake source</span>
                    <select class="ppu__input" [(ngModel)]="createForm.intakeSource" name="podcastIntake"
                            [disabled]="creating">
                        <option *ngFor="let s of intakeSources" [value]="s">{{ s }}</option>
                    </select>
                </label>
                <label class="ppu__field ppu__field--grow"
                       *ngIf="createForm.intakeSource === 'Box'">
                    <span class="ppu__label">Box folder reference</span>
                    <input class="ppu__input" type="text" [(ngModel)]="createForm.boxFolderRef"
                           name="podcastBoxFolderRef" [disabled]="creating"
                           placeholder="Box folder ID or path" maxlength="500" />
                </label>
            </div>

            <div *ngIf="createState === 'error'" class="ppu__banner ppu__banner--err">
                <i class="fa-solid fa-circle-exclamation"></i>
                <span>{{ createMessage }}</span>
            </div>
        </div>

        <div foot>
            <span class="ppu__hint">Operator+ — see #938.</span>
            <button type="button" class="ppu__submit" (click)="onCreatePodcast()"
                    [disabled]="!canCreatePodcast">
                <i class="fa-solid"
                   [class.fa-plus]="!creating"
                   [class.fa-spinner]="creating"
                   [class.fa-spin]="creating"></i>
                {{ creating ? 'Creating…' : 'Create podcast' }}
            </button>
        </div>
    </mkt-ws-card>

    <mkt-ws-card *ngIf="podcasts.length > 0" [hasHead]="true" [hasFoot]="true">
        <div head><span class="ppu__caps">New episode</span></div>

        <div class="ppu__form">
            <label class="ppu__field">
                <span class="ppu__label">Podcast</span>
                <select class="ppu__input" [(ngModel)]="form.podcastId" name="podcastId" [disabled]="busy">
                    <option *ngFor="let p of podcasts" [value]="p.ID">{{ p.Name }}<span *ngIf="p.IntakeSource"> · {{ p.IntakeSource }}</span></option>
                </select>
            </label>

            <label class="ppu__field">
                <span class="ppu__label">Episode title <span class="ppu__req">*</span></span>
                <input class="ppu__input" type="text" [(ngModel)]="form.title" name="title" [disabled]="busy" placeholder="e.g. Episode 126 · Meet Grace" maxlength="500" />
            </label>

            <div class="ppu__field-row">
                <label class="ppu__field ppu__field--narrow">
                    <span class="ppu__label">Episode #</span>
                    <input class="ppu__input" type="number" [(ngModel)]="form.episodeNumber" name="episodeNumber" [disabled]="busy" placeholder="126" min="0" />
                </label>
                <label class="ppu__field ppu__field--narrow">
                    <span class="ppu__label">Published at</span>
                    <input class="ppu__input" type="date" [(ngModel)]="form.publishedAt" name="publishedAt" [disabled]="busy" />
                </label>
            </div>

            <label class="ppu__field">
                <span class="ppu__label">Audio file</span>
                <input class="ppu__file" type="file" accept="audio/*,.mp3,.m4a,.wav" (change)="onAudioChosen($event)" [disabled]="busy" />
                <span *ngIf="audioFileName" class="ppu__hint ppu__hint--ok">
                    <i class="fa-solid fa-file-audio"></i> {{ audioFileName }} · {{ audioSizeKb | number }} KB
                </span>
                <span *ngIf="audioError" class="ppu__hint ppu__hint--err">{{ audioError }}</span>
                <span *ngIf="!audioFileName && !audioError" class="ppu__hint">Up to 25 MB. Larger files should be dropped directly into the podcast's Box folder.</span>
            </label>

            <label class="ppu__field">
                <span class="ppu__label">Transcript (optional)</span>
                <textarea class="ppu__input ppu__input--textarea" [(ngModel)]="form.transcriptText" name="transcriptText" [disabled]="busy" rows="6" placeholder="Paste a diarized transcript here. With speaker labels, the episode skips the diarize step."></textarea>
                <span class="ppu__hint">If omitted, the cron will diarize the audio on the next pass.</span>
            </label>

            <div *ngIf="state === 'done'" class="ppu__banner ppu__banner--ok">
                <i class="fa-solid fa-circle-check"></i>
                <span>{{ resultMessage }}</span>
            </div>
            <div *ngIf="state === 'error'" class="ppu__banner ppu__banner--err">
                <i class="fa-solid fa-circle-exclamation"></i>
                <span>{{ resultMessage }}</span>
            </div>
        </div>

        <div foot>
            <span class="ppu__hint">Operator+ — see #938.</span>
            <button type="button" class="ppu__submit" (click)="onSubmit()" [disabled]="!canSubmit">
                <i class="fa-solid" [class.fa-arrow-up-from-bracket]="!busy" [class.fa-spinner]="busy" [class.fa-spin]="busy"></i>
                {{ submitLabel }}
            </button>
        </div>
    </mkt-ws-card>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .ppu__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .ppu__empty { padding: 24px 0; }
        .ppu__caps { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .ppu__form { display: flex; flex-direction: column; gap: 16px; padding: 4px 0; }
        .ppu__field { display: flex; flex-direction: column; gap: 6px; }
        .ppu__field--narrow { max-width: 200px; }
        .ppu__field--grow { flex: 1; min-width: 220px; }
        .ppu__field-row { display: flex; gap: 16px; flex-wrap: wrap; align-items: flex-end; }
        .ppu__empty-lead { display: flex; align-items: center; gap: 10px; padding: 0 0 12px; margin: 0 0 12px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); font-size: 13.5px; color: var(--mj-text-secondary, #71717a); }
        .ppu__empty-lead i { color: var(--mj-text-secondary, #71717a); }
        .ppu__label { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); font-weight: 500; }
        .ppu__req { color: var(--mj-danger, #dc2626); }
        .ppu__input { font-family: inherit; font-size: 13px; padding: 8px 10px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); }
        .ppu__input:focus { outline: none; border-color: var(--mj-brand-primary, #6366f1); box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15); }
        .ppu__input--textarea { resize: vertical; min-height: 100px; }
        .ppu__file { font-size: 12px; padding: 4px 0; }
        .ppu__hint { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .ppu__hint--ok { color: var(--mj-text-primary, #1a1a1a); }
        .ppu__hint--err { color: var(--mj-danger, #dc2626); }
        .ppu__banner { display: flex; gap: 8px; align-items: center; padding: 10px 12px; border-radius: 6px; font-size: 12.5px; }
        .ppu__banner--ok { background: rgba(22, 163, 74, 0.1); color: var(--mj-success, #15803d); }
        .ppu__banner--err { background: rgba(220, 38, 38, 0.1); color: var(--mj-danger, #b91c1c); }
        .ppu__submit { display: inline-flex; align-items: center; gap: 6px; font-family: inherit; font-size: 12.5px; font-weight: 500; padding: 8px 14px; border: none; border-radius: 6px; background: var(--mj-brand-primary, #6366f1); color: #fff; cursor: pointer; transition: opacity 0.15s; }
        .ppu__submit:disabled { opacity: 0.5; cursor: not-allowed; }
        .ppu__submit:hover:not(:disabled) { opacity: 0.9; }
    `]
})
export class ProgramsPodcastUploadSection implements OnInit, OnChanges {
    @Input() companyId = '';
    @Input() companyName = '';

    private cdr = inject(ChangeDetectorRef);

    podcasts: PodcastRow[] = [];
    loading = false;

    form: { podcastId: string; title: string; episodeNumber: number | null; publishedAt: string; transcriptText: string } = {
        podcastId: '', title: '', episodeNumber: null, publishedAt: '', transcriptText: '',
    };

    private audioBase64: string | null = null;
    audioFileName = '';
    audioSizeKb = 0;
    audioError = '';

    state: SubmitState = 'idle';
    resultMessage = '';

    /** Inline create-podcast form (#938 follow-up). Shown when the company
     *  has zero Podcasts configured so the operator has a UX path to create
     *  one without leaving the section. */
    readonly intakeSources: ReadonlyArray<PodcastIntakeSource> = INTAKE_SOURCES;
    createForm: { name: string; description: string; intakeSource: PodcastIntakeSource; boxFolderRef: string } = {
        name: '', description: '', intakeSource: 'Manual', boxFolderRef: '',
    };
    createState: 'idle' | 'creating' | 'error' = 'idle';
    createMessage = '';

    private static readonly MAX_AUDIO_BYTES = 25 * 1024 * 1024;

    ngOnInit(): void {
        if (this.companyId) void this.loadPodcasts();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['companyId'] && this.companyId) void this.loadPodcasts();
    }

    get busy(): boolean { return this.state === 'reading' || this.state === 'uploading'; }
    get creating(): boolean { return this.createState === 'creating'; }
    get canCreatePodcast(): boolean {
        if (this.creating) return false;
        if (!this.createForm.name.trim()) return false;
        return true;
    }
    get canSubmit(): boolean {
        if (this.busy) return false;
        if (!this.form.podcastId) return false;
        if (!this.form.title.trim()) return false;
        if (!this.audioBase64 && !this.form.transcriptText.trim()) return false;
        return true;
    }
    get submitLabel(): string {
        if (this.state === 'reading') return 'Reading file…';
        if (this.state === 'uploading') return 'Uploading…';
        return 'Upload episode';
    }

    private async loadPodcasts(): Promise<void> {
        this.loading = true;
        try {
            const rv = new RunView();
            const result = await rv.RunView<marketingPodcastEntity>({
                EntityName: 'Podcasts',
                ExtraFilter: `CompanyID='${this.companyId}'`,
                OrderBy: 'Name',
                MaxRows: 100,
                ResultType: 'simple',
            });
            if (!result.Success) {
                LogError(`[ProgramsPodcastUpload] Podcast lookup failed: ${result.ErrorMessage}`);
                this.podcasts = [];
            } else {
                this.podcasts = (result.Results ?? []).map(r => ({
                    ID: String((r as unknown as { ID: unknown }).ID ?? ''),
                    Name: String((r as unknown as { Name: unknown }).Name ?? ''),
                    BoxFolderRef: ((r as unknown as { BoxFolderRef: unknown }).BoxFolderRef as string | null) ?? null,
                    IntakeSource: ((r as unknown as { IntakeSource: unknown }).IntakeSource as string | null) ?? null,
                }));
                if (this.podcasts.length > 0 && !this.form.podcastId) {
                    this.form.podcastId = this.podcasts[0].ID;
                }
            }
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    onAudioChosen(event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0] ?? null;
        this.audioBase64 = null;
        this.audioFileName = '';
        this.audioSizeKb = 0;
        this.audioError = '';

        if (!file) return;
        if (file.size > ProgramsPodcastUploadSection.MAX_AUDIO_BYTES) {
            this.audioError = `File is ${Math.round(file.size / 1024 / 1024)} MB — limit is 25 MB. Drop large files directly into Box.`;
            return;
        }

        this.state = 'reading';
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result === 'string') {
                const comma = result.indexOf(',');
                this.audioBase64 = comma >= 0 ? result.slice(comma + 1) : result;
                this.audioFileName = file.name;
                this.audioSizeKb = Math.round(file.size / 1024);
            }
            this.state = 'idle';
            this.cdr.detectChanges();
        };
        reader.onerror = () => {
            this.audioError = 'Failed to read file.';
            this.state = 'idle';
            this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
    }

    async onSubmit(): Promise<void> {
        if (!this.canSubmit) return;
        this.state = 'uploading';
        this.resultMessage = '';
        this.cdr.detectChanges();

        const params: { Name: string; Value: string }[] = [
            { Name: 'PodcastID', Value: this.form.podcastId },
            { Name: 'Title', Value: this.form.title.trim() },
        ];
        if (this.form.episodeNumber != null) params.push({ Name: 'EpisodeNumber', Value: String(this.form.episodeNumber) });
        if (this.form.publishedAt) params.push({ Name: 'PublishedAt', Value: this.form.publishedAt });
        if (this.audioBase64) {
            params.push({ Name: 'AudioBase64', Value: this.audioBase64 });
            params.push({ Name: 'AudioFileName', Value: this.audioFileName });
        }
        if (this.form.transcriptText.trim()) {
            params.push({ Name: 'TranscriptText', Value: this.form.transcriptText.trim() });
        }

        const result = await runAction('Upload Manual Podcast Episode', params);
        if (result.success) {
            this.state = 'done';
            this.resultMessage = result.message || 'Episode created.';
            this.resetForm();
        } else {
            this.state = 'error';
            this.resultMessage = result.message || 'Upload failed.';
        }
        this.cdr.detectChanges();
    }

    private resetForm(): void {
        this.form = { podcastId: this.form.podcastId, title: '', episodeNumber: null, publishedAt: '', transcriptText: '' };
        this.audioBase64 = null;
        this.audioFileName = '';
        this.audioSizeKb = 0;
    }

    /** Inline create-podcast handler (#938 follow-up). Saves a new
     *  marketing.Podcast row for the active company, then re-loads the
     *  dropdown so the operator can immediately upload an episode against
     *  the new podcast. Operator+ permission is enforced at the route
     *  level — no in-component re-check.
     */
    async onCreatePodcast(): Promise<void> {
        if (!this.canCreatePodcast) return;
        if (!this.companyId) {
            this.createState = 'error';
            this.createMessage = 'No company selected.';
            this.cdr.detectChanges();
            return;
        }
        this.createState = 'creating';
        this.createMessage = '';
        this.cdr.detectChanges();
        try {
            const md = new Metadata();
            const entity = await md.GetEntityObject<marketingPodcastEntity>('Podcasts');
            entity.NewRecord();
            entity.CompanyID = this.companyId;
            entity.Name = this.createForm.name.trim();
            const desc = this.createForm.description.trim();
            if (desc) entity.Description = desc;
            // The IntakeSource enum union on the generated entity has
            // duplicate literals; cast through the codegen drift to keep
            // the typed assignment without a Get/Set fallback. WT-938.
            entity.IntakeSource = this.createForm.intakeSource as unknown as typeof entity.IntakeSource;
            if (this.createForm.intakeSource === 'Box') {
                const ref = this.createForm.boxFolderRef.trim();
                if (ref) entity.BoxFolderRef = ref;
            }
            entity.IsActive = true;
            const ok = await entity.Save();
            if (!ok) {
                const msg = entity.LatestResult?.Message ?? 'unknown error';
                LogError(`[ProgramsPodcastUpload] Podcast create failed: ${msg}`);
                this.createState = 'error';
                this.createMessage = `Create failed: ${msg}`;
                return;
            }
            // Reset the create form, refresh the podcast list, and pre-select
            // the new podcast so the operator can immediately upload.
            const newPodcastId = entity.ID;
            this.createForm = { name: '', description: '', intakeSource: 'Manual', boxFolderRef: '' };
            this.createState = 'idle';
            await this.loadPodcasts();
            if (this.podcasts.some(p => p.ID === newPodcastId)) {
                this.form.podcastId = newPodcastId;
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ProgramsPodcastUpload] onCreatePodcast threw: ${msg}`);
            this.createState = 'error';
            this.createMessage = `Create failed: ${msg}`;
        } finally {
            this.cdr.detectChanges();
        }
    }
}
